import { Trash2 } from 'lucide-react';
import { BUILT_IN_KEYS } from './subItemTypes';
import type { CostBreakdown } from '@jobos/shared';
import { formatCurrency, formatPercent } from '@/lib/utils';
import { useSubItemCategoriesContext } from '@/contexts/SubItemCategoriesContext';

interface CostBreakdownSummaryProps {
  breakdown: CostBreakdown;
  taxRate: number;
  markupRate: number;
  onRemoveRounding?: () => void;
}

export function CostBreakdownSummary({ breakdown, taxRate, markupRate, onRemoveRounding }: CostBreakdownSummaryProps) {
  const { categories } = useSubItemCategoriesContext();
  const hasRounding = Math.abs(breakdown.rounding) >= 0.005;

  // Dynamic category rows (skip profit/discount - rendered separately below)
  const categoryRows = categories.filter(
    (cat) => cat.key !== BUILT_IN_KEYS.PROFIT && cat.key !== BUILT_IN_KEYS.DISCOUNT
  );

  return (
    <div className="border-t border-gray-200 mt-2 pt-4 space-y-2 px-3">
      {categoryRows.map((cat) => {
        const value = breakdown.categoryTotals[cat.key] || 0;
        if (value === 0) return null;
        return <SummaryRow key={cat.key} label={cat.key === 'labor' ? 'Labor Cost' : cat.label} value={value} />;
      })}
      {breakdown.tax > 0 && (
        <SummaryRow label={`Tax (${formatPercent(taxRate)})`} value={breakdown.tax} />
      )}
      <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center text-sm leading-6 border-t border-gray-100 pt-1">
        <span className="text-gray-500">Subtotal</span>
        <span className="text-gray-700 text-right tabular-nums">
          {formatCurrency(breakdown.subtotal)}
        </span>
      </div>
      {breakdown.jobMarkup > 0 && (
        <SummaryRow
          label={`Markup (${formatPercent(markupRate)})`}
          value={breakdown.jobMarkup}
        />
      )}
      {breakdown.customMarkup > 0 && (
        <SummaryRow
          label="Custom markup *"
          value={breakdown.customMarkup}
        />
      )}
      <SummaryRow label="+ Profit" value={breakdown.profit} />
      {breakdown.discount > 0 && (
        <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center text-sm leading-6">
          <span className="text-gray-500">- Discount</span>
          <span className="text-red-600 text-right tabular-nums">
            -{formatCurrency(breakdown.discount)}
          </span>
        </div>
      )}
      {hasRounding && (
        <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center text-sm leading-6">
          <span className="text-gray-500 flex items-center gap-1">
            Rounding
            {onRemoveRounding && (
              <button
                onClick={onRemoveRounding}
                className="text-gray-400 hover:text-red-500 transition-colors p-0.5"
                title="Remove rounding"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </span>
          <span className={`text-right tabular-nums ${breakdown.rounding >= 0 ? 'text-gray-700' : 'text-red-600'}`}>
            {breakdown.rounding >= 0 ? '+' : '-'}{formatCurrency(Math.abs(breakdown.rounding))}
          </span>
        </div>
      )}
      <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center text-sm leading-6 font-semibold border-t border-gray-200 pt-2 mt-2">
        <span className="text-gray-700">Total</span>
        <span className="text-gray-900 text-right tabular-nums">
          {formatCurrency(breakdown.roundedTotal)}
        </span>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_6rem] items-center text-sm leading-6">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-700 text-right tabular-nums">
        {formatCurrency(value)}
      </span>
    </div>
  );
}
